/**
 * Desktop mirror of web activation events (same names + once semantics).
 * Posts to DESKTOP_WEB_APP_URL/api/activation when configured.
 */

export type ActivationEventName =
  | "activation.chapter_written"
  | "activation.story_craft_run"
  | "activation.variation_applied"
  | "activation.export_done"
  | "activation.listen_done"
  | "activation.compare_page_view"
  | "activation.planning_opened"
  | "activation.scene_tool_used"
  | "activation.publish_wizard_completed";

export type ActivationEventPayload = {
  event: ActivationEventName;
  userId?: string | null;
  bookId?: string | null;
  chapterId?: string | null;
  source?: "desktop";
  metadata?: Record<string, unknown>;
  occurredAt?: string;
};

const SESSION_KEY = "jw_activation_once";

function shouldTrackOnce(event: ActivationEventName, bookId?: string | null): boolean {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    const seen = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    const key = `${event}:${bookId || "global"}`;
    if (seen[key]) return false;
    seen[key] = true;
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(seen));
    return true;
  } catch {
    return true;
  }
}

export async function trackDesktopActivation(
  payload: ActivationEventPayload,
  options?: { once?: boolean; endpoint?: string },
): Promise<void> {
  try {
    if (options?.once !== false && !shouldTrackOnce(payload.event, payload.bookId)) {
      return;
    }
    const base =
      options?.endpoint ||
      (typeof window !== "undefined" &&
        (window as unknown as { desktopV2Api?: { getWebAppUrl?: () => Promise<string> } }).desktopV2Api
          ?.getWebAppUrl) ||
      null;
    let endpoint = options?.endpoint;
    if (!endpoint && base && typeof base === "function") {
      const origin = await base();
      endpoint = `${String(origin).replace(/\/$/, "")}/api/activation`;
    }
    if (!endpoint) {
      // Fall back to production host for packaged builds without IPC helper.
      endpoint = "https://heyjustwrite.com/api/activation";
    }
    await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        source: "desktop",
        occurredAt: payload.occurredAt || new Date().toISOString(),
      }),
      keepalive: true,
    });
  } catch {
    // never block writing
  }
}
